import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { promotionApiService, Promotion, PromotionStats, CreatePromotionData } from '../services/promotionApi';
import { serviceApiService, Service } from '../services/serviceApi';
import { formatDate, formatDateRange } from '../utils/dateUtils';

interface PromotionManagerProps {
  onClose: () => void;
}

const PromotionManager: React.FC<PromotionManagerProps> = ({ onClose }) => {
  const [promotions, setPromotions] = useState<Promotion[]>([]);
  const [stats, setStats] = useState<PromotionStats | null>(null);
  const [services, setServices] = useState<Service[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingPromotion, setEditingPromotion] = useState<Promotion | null>(null);
  const [formData, setFormData] = useState<CreatePromotionData>({
    name: '',
    code: '',
    description: '',
    discount_type: 'percentage',
    discount_value: 0,
    buy_quantity: undefined,
    get_quantity: undefined,
    buy_service_id: undefined,
    get_service_id: undefined,
    status: 'active',
    expiration_date: '',
    is_public: false
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Charger les promotions et services
      const [promotionsData, servicesData] = await Promise.all([
        promotionApiService.getPromotions(),
        serviceApiService.getServices()
      ]);
      
      setPromotions(Array.isArray(promotionsData) ? promotionsData : []);
      setServices(Array.isArray(servicesData) ? servicesData : []);
      
      // Charger les stats (optionnel - peut échouer si non admin)
      try {
        const statsData = await promotionApiService.getPromotionStats();
        setStats(statsData);
      } catch (statsErr) {
        console.log('Stats promotions non disponibles (authentification requise)');
        setStats(null);
      }
    } catch (err: any) {
      console.error('Erreur lors du chargement des données:', err);
      setError(err.message || 'Erreur lors du chargement des données');
      setPromotions([]);
      setStats(null);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const errors = promotionApiService.validatePromotionData(formData);
    if (errors.length > 0) {
      setError(errors.join(', '));
      return;
    }

    try {
      if (editingPromotion) {
        await promotionApiService.updatePromotion(editingPromotion.id, formData);
      } else {
        await promotionApiService.createPromotion(formData);
      }
      
      await loadData();
      resetForm();
      setError(null);
    } catch (err: any) {
      setError(err.message);
    }
  };

  const handleDelete = async (promotion: Promotion) => {
    if (!window.confirm(`Êtes-vous sûr de vouloir supprimer la promotion "${promotion.name}" ?`)) {
      return;
    }

    try {
      await promotionApiService.deletePromotion(promotion.id);
      await loadData();
      setError(null);
    } catch (err: any) {
      setError(err.message);
    }
  };

  const handleToggleStatus = async (promotion: Promotion) => {
    try {
      await promotionApiService.togglePromotionStatus(promotion.id);
      await loadData();
      setError(null);
    } catch (err: any) {
      setError(err.message);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      code: '',
      description: '',
      discount_type: 'percentage',
      discount_value: 0,
      buy_quantity: undefined,
      get_quantity: undefined,
      buy_service_id: undefined,
      get_service_id: undefined,
      status: 'active',
      expiration_date: '',
      is_public: false
    });
    setEditingPromotion(null);
    setShowAddModal(false);
  };

  const startEdit = (promotion: Promotion) => {
    setFormData({
      name: promotion.name,
      code: promotion.code,
      description: promotion.description || '',
      discount_type: promotion.discount_type,
      discount_value: promotion.discount_value,
      buy_quantity: promotion.buy_quantity,
      get_quantity: promotion.get_quantity,
      buy_service_id: promotion.buy_service_id,
      get_service_id: promotion.get_service_id,
      status: promotion.status,
      expiration_date: promotion.expiration_date ? promotion.expiration_date.slice(0, 16) : '',
      is_public: promotion.is_public || false
    });
    setEditingPromotion(promotion);
    setShowAddModal(true);
  };

  const generateCode = () => {
    const code = promotionApiService.generatePromotionCode();
    setFormData({ ...formData, code });
  };

  const getServiceName = (serviceId: number | undefined) => {
    if (!serviceId) return 'Service non défini';
    const service = services.find(s => s.id === serviceId.toString());
    return service ? service.name : `Service #${serviceId}`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Statistiques */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-blue-50 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-blue-600 text-sm font-medium">Total Promotions</p>
                <p className="text-2xl font-bold text-blue-900">{stats.total_promotions}</p>
              </div>
              <span className="text-2xl">🎯</span>
            </div>
          </div>
          
          <div className="bg-green-50 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-green-600 text-sm font-medium">Promotions Actives</p>
                <p className="text-2xl font-bold text-green-900">{stats.active_promotions}</p>
              </div>
              <span className="text-2xl">✅</span>
            </div>
          </div>
          
          <div className="bg-purple-50 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-purple-600 text-sm font-medium">Valides Maintenant</p>
                <p className="text-2xl font-bold text-purple-900">{stats.valid_promotions}</p>
              </div>
              <span className="text-2xl">⏰</span>
            </div>
          </div>
          
          <div className="bg-red-50 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-red-600 text-sm font-medium">Expirées</p>
                <p className="text-2xl font-bold text-red-900">{stats.expired_promotions}</p>
              </div>
              <span className="text-2xl">⏳</span>
            </div>
          </div>
        </div>
      )}

      {/* Actions */}
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold text-gray-900">Promotions ({promotions?.length || 0})</h2>
        <button
          onClick={() => setShowAddModal(true)}
          className="bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 transition-colors flex items-center space-x-2"
        >
          <span>🎯</span>
          <span>Ajouter une promotion</span>
        </button>
      </div>

      {/* Messages d'erreur */}
      {error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-red-700">{error}</p>
          <button
            onClick={() => setError(null)}
            className="mt-2 text-red-600 hover:text-red-800 text-sm underline"
          >
            Fermer
          </button>
        </div>
      )}

      {/* Liste des promotions */}
      {!promotions || promotions.length === 0 ? (
        <div className="text-center py-12">
          <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <span className="text-2xl">🎯</span>
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">Aucune promotion</h3>
          <p className="text-gray-600 mb-4">Commencez par créer votre première promotion.</p>
          <button
            onClick={() => setShowAddModal(true)}
            className="bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 transition-colors"
          >
            Créer une promotion
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {promotions.map((promotion) => (
            <motion.div
              key={promotion.id}
              layout
              className="bg-gray-50 rounded-lg p-6 border border-gray-200 hover:shadow-md transition-shadow"
            >
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1">
                  <div className="flex items-center space-x-2 mb-2">
                    <h3 className="font-semibold text-gray-900">{promotion.name}</h3>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      promotion.status === 'active' 
                        ? 'bg-green-100 text-green-800' 
                        : promotion.status === 'expired'
                        ? 'bg-red-100 text-red-800'
                        : 'bg-gray-100 text-gray-800'
                    }`}>
                      {promotion.status === 'active' ? 'Active' : 
                       promotion.status === 'expired' ? 'Expirée' : 
                       promotion.status === 'inactive' ? 'Inactive' : 'Programmée'}
                    </span>
                    {promotion.is_valid_now && (
                      <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-xs font-medium">
                        Valide
                      </span>
                    )}
                  </div>
                  <p className="text-gray-600 text-sm mb-3">{promotion.description}</p>
                  <div className="space-y-1">
                    <p className="text-lg font-bold text-purple-600">{promotion.discount_display}</p>
                    <p className="text-sm font-mono bg-gray-200 px-2 py-1 rounded inline-block">{promotion.code}</p>
                    <p className="text-xs text-gray-500">
                      {formatDateRange(promotion.start_date, promotion.end_date)}
                    </p>
                    {promotion.max_uses && (
                      <p className="text-xs text-gray-500">
                        Utilisations: {promotion.current_uses}/{promotion.max_uses}
                      </p>
                    )}
                  </div>
                </div>
              </div>
              
              <div className="flex items-center justify-between pt-4 border-t border-gray-200">
                <div className="flex space-x-2">
                  <button
                    onClick={() => startEdit(promotion)}
                    className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                    title="Modifier"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                    </svg>
                  </button>
                  
                  <button
                    onClick={() => handleToggleStatus(promotion)}
                    className={`p-2 rounded-lg transition-colors ${
                      promotion.status === 'active'
                        ? 'text-red-600 hover:bg-red-50'
                        : 'text-green-600 hover:bg-green-50'
                    }`}
                    title={promotion.status === 'active' ? 'Désactiver' : 'Activer'}
                  >
                    {promotion.status === 'active' ? (
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728L5.636 5.636m12.728 12.728L5.636 5.636" />
                      </svg>
                    ) : (
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    )}
                  </button>
                  
                  <button
                    onClick={() => handleDelete(promotion)}
                    className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                    title="Supprimer"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </button>
                </div>
                
                <p className="text-xs text-gray-400">
                  {formatDate(promotion.updated_at)}
                </p>
              </div>
            </motion.div>
          ))}
        </div>
      )}

      {/* Modal d'ajout/modification */}
      <AnimatePresence>
        {showAddModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-60 flex items-center justify-center p-4"
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
            >
              <div className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-xl font-bold text-gray-900">
                    {editingPromotion ? 'Modifier la promotion' : 'Créer une promotion'}
                  </h2>
                  <button
                    onClick={resetForm}
                    className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>

                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Nom de la promotion *
                      </label>
                      <input
                        type="text"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                        placeholder="Ex: Réduction Black Friday"
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Code promo *
                      </label>
                      <div className="flex space-x-2">
                        <input
                          type="text"
                          value={formData.code}
                          onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                          className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                          placeholder="PROMO2024"
                          required
                        />
                        <button
                          type="button"
                          onClick={generateCode}
                          className="px-3 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
                          title="Générer un code"
                        >
                          🎲
                        </button>
                      </div>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Description *
                    </label>
                    <textarea
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      placeholder="Décrivez la promotion..."
                      required
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Type de réduction *
                      </label>
                      <select
                        value={formData.discount_type}
                        onChange={(e) => setFormData({ ...formData, discount_type: e.target.value as any })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      >
                        <option value="percentage">Pourcentage</option>
                        <option value="fixed_amount">Montant fixe</option>
                        <option value="buy_x_get_y">Achetez X obtenez Y</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Valeur de réduction *
                      </label>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={formData.discount_value}
                        onChange={(e) => setFormData({ ...formData, discount_value: parseFloat(e.target.value) || 0 })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                        placeholder={formData.discount_type === 'percentage' ? '10' : '5000'}
                        required
                      />
                    </div>
                  </div>

                  {formData.discount_type === 'buy_x_get_y' && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Service à acheter (X) *
                        </label>
                        <select
                          value={formData.buy_service_id || ''}
                          onChange={(e) => setFormData({ ...formData, buy_service_id: e.target.value || undefined })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                          required
                        >
                          <option value="">Sélectionnez un service</option>
                          {services.map((service) => (
                            <option key={service.id} value={service.id}>
                              {service.name} - {service.price} FCFA
                            </option>
                          ))}
                        </select>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Service gratuit (Y) *
                        </label>
                        <select
                          value={formData.get_service_id || ''}
                          onChange={(e) => setFormData({ ...formData, get_service_id: e.target.value || undefined })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                          required
                        >
                          <option value="">Sélectionnez un service</option>
                          {services.map((service) => (
                            <option key={service.id} value={service.id}>
                              {service.name} - {service.price} FCFA
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                  )}

                  <div className="flex items-center space-x-4">
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="is_public"
                        checked={formData.is_public}
                        onChange={(e) => setFormData({ ...formData, is_public: e.target.checked })}
                        className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
                      />
                      <label htmlFor="is_public" className="ml-2 block text-sm text-gray-700">
                        Visible publiquement
                      </label>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Statut
                      </label>
                      <select
                        value={formData.status}
                        onChange={(e) => setFormData({ ...formData, status: e.target.value as any })}
                        className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      >
                        <option value="active">Active</option>
                        <option value="inactive">Inactive</option>
                        <option value="scheduled">Programmée</option>
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Date d'expiration
                    </label>
                    <input
                      type="datetime-local"
                      value={formData.expiration_date}
                      onChange={(e) => setFormData({ ...formData, expiration_date: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    />
                    <p className="text-xs text-gray-500 mt-1">Optionnel - Date limite d'utilisation</p>
                  </div>

                  <div className="flex space-x-3 pt-4">
                    <button
                      type="button"
                      onClick={resetForm}
                      className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                    >
                      Annuler
                    </button>
                    <button
                      type="submit"
                      className="flex-1 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
                    >
                      {editingPromotion ? 'Modifier' : 'Créer'}
                    </button>
                  </div>
                </form>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default PromotionManager;
